import { Auth } from 'aws-amplify';
import store from '../store';
import { logOutUser } from '../store/thunk/auth';
import { setNotifications } from '../store/slices/notifications';
import { NO_ACCESS_MESSAGE } from '../auth/messages';
import { forceRefreshedAccessToken, isNoSessionError, isNotAuthorizedError } from './auth';

export { NO_ACCESS_MESSAGE };

export class ApiError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

/** 403 from our API: the user is signed in but the backend denied this call. */
export class ForbiddenError extends ApiError {
    url: string;
    method: string;
    /** Raw response body, kept for diagnostics (the toast stays generic). */
    detail: string;

    constructor(url: string, method: string, detail: string) {
        super(NO_ACCESS_MESSAGE, 403);
        this.name = 'ForbiddenError';
        this.url = url;
        this.method = method;
        this.detail = detail;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

let logoutInFlight = false;
/** Signs out once even when several parallel requests (or Hub events) fail at the same time. */
export const triggerLogout = () => {
    if (logoutInFlight || !store.getState().auth.data.isLoggedIn) return;
    logoutInFlight = true;
    Promise.resolve(store.dispatch(logOutUser())).finally(() => {
        logoutInFlight = false;
    });
};

const notifyNoAccess = () => {
    store.dispatch(setNotifications({ text: NO_ACCESS_MESSAGE, type: 'error' }));
};

const currentAccessToken = async (): Promise<string> =>
    (await Auth.currentSession()).getAccessToken().getJwtToken();

/**
 * Amplify refreshes the access token with the refresh token whenever the cached one has expired,
 * so a request never goes out with a stale token. A dead session (refresh token rejected, signed
 * out in another tab) is thrown; a transient failure falls back to the last known token and lets
 * the server's answer decide.
 */
const accessToken = async (): Promise<string> => {
    try {
        return await currentAccessToken();
    } catch (e) {
        if (isNotAuthorizedError(e) || isNoSessionError(e)) throw e;
        const stored = store.getState().auth.data.token;
        if (stored) return stored;
        throw e;
    }
};

/** After a 401: reuse a token another caller already refreshed, otherwise ask Cognito once. */
const tokenAfter401 = async (rejected: string): Promise<string> => {
    try {
        const current = await currentAccessToken();
        if (current && current !== rejected) return current;
    } catch (e) {
        /* fall through to a forced refresh */
    }
    return forceRefreshedAccessToken();
};

const withAuth = (init: RequestInit, token: string): RequestInit => {
    const headers = new Headers(init.headers ?? undefined);
    headers.set('Authorization', `Bearer ${encodeURIComponent(token)}`);
    return { ...init, headers };
};

const unauthorized = (): never => {
    triggerLogout();
    throw new ApiError('Unauthorized!', 401);
};

/**
 * `fetch` with the Cognito access token attached.
 *  - 401: refresh the session (single-flight) and retry once; a second 401 signs the user out.
 *  - 403: shows the "no access" notification and throws ForbiddenError (no sign-out).
 *  - every other status is returned to the caller untouched.
 */
export const apiFetch = async (input: string, init: RequestInit = {}): Promise<Response> => {
    let token: string;
    try {
        token = await accessToken();
    } catch (e) {
        return unauthorized();
    }
    let res = await fetch(input, withAuth(init, token));
    if (res.status === 401) {
        try {
            token = await tokenAfter401(token);
        } catch (e) {
            return unauthorized();
        }
        res = await fetch(input, withAuth(init, token));
    }
    if (res.status === 401) return unauthorized();
    if (res.status === 403) {
        const method = init.method ?? 'GET';
        const detail = await res.clone().text().catch(() => '');
        console.warn(`[api] 403 ${method} ${input}`, detail);
        notifyNoAccess();
        throw new ForbiddenError(input, method, detail);
    }
    return res;
};
